import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link as ChakraLink,
  useToast,
  useColorModeValue,
  Alert,
  AlertIcon,
  VStack,
  Textarea,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import env from '../../config/env';
import { useAuth } from '../../contexts/AuthContext';
import CryptoJS from 'crypto-js';

const RecoveryPage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'request' | 'recover'>(searchParams.get('code') ? 'recover' : 'request');
  const [email, setEmail] = useState('');
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [recoveryToken, setRecoveryToken] = useState('');
  const [encryptedVerification, setEncryptedVerification] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const { deriveMasterKeyFromRecovery, verifyRecoveryPhrase, encryptMasterKey, login } = useAuth();

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      verifyCode(code);
    }
  }, [searchParams]);

  const verifyCode = async (code: string) => {
    try {
      const response = await axios.post(`${env.API_URL}/auth/recovery/verify`, { code });
      setVerifiedEmail(response.data.email);
      setEmail(response.data.email);
      setRecoveryToken(response.data.recoveryToken);
      setEncryptedVerification(response.data.encryptedVerification);
    } catch (error) {
      toast({
        title: 'Invalid Recovery Link',
        description: 'This recovery link is invalid or has expired. Please request a new one.',
        status: 'error',
        duration: 5000,
      });
      setStep('request');
    }
  };

  const handleRequestRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${env.API_URL}/auth/recovery/request`, { email });
      
      toast({
        title: 'Recovery Email Sent',
        description: 'If an account exists with that email, we\'ve sent recovery instructions.',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      toast({
        title: 'Request Failed',
        description: 'Could not process recovery request. Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!recoveryToken) {
        throw new Error('Invalid recovery session');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (newPassword.length < 12) {
        throw new Error('Password must be at least 12 characters long');
      }

      // Verify recovery phrase
      if (!verifyRecoveryPhrase(recoveryPhrase)) {
        throw new Error('Invalid recovery phrase');
      }

      // Derive master key from recovery phrase
      const masterKey = deriveMasterKeyFromRecovery(recoveryPhrase);

      // Verify master key by decrypting the verification string
      let decryptedVerification: string | null = null;
      try {
        decryptedVerification = CryptoJS.AES.decrypt(encryptedVerification, masterKey).toString(CryptoJS.enc.Utf8);
        if (!decryptedVerification) {
          throw new Error('Invalid recovery phrase');
        }
      } catch (error) {
        throw new Error('Invalid recovery phrase');
      }

      // Encrypt master key with new password
      const newEncryptedMasterKey = encryptMasterKey(masterKey, newPassword);

      // Complete recovery
      await axios.post(`${env.API_URL}/auth/recovery/complete`, {
        recoveryToken,
        newPassword,
        newEncryptedMasterKey,
        decryptedVerification: decryptedVerification
      });

      // Log in with new credentials
      await login(verifiedEmail, newPassword);

      toast({
        title: 'Account Recovered',
        description: 'Your account has been recovered successfully',
        status: 'success',
        duration: 5000,
      });

      navigate('/journal');
    } catch (error: any) {
      toast({
        title: 'Recovery Failed',
        description: error.response?.data?.error || error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Stack spacing="8">
        <Stack spacing="6">
          <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
            <Heading size={{ base: 'xs', md: 'sm' }}>Account Recovery</Heading>
            <Text color="muted">
              {step === 'request' 
                ? 'Enter your email to receive recovery instructions'
                : 'Enter your recovery phrase and new password'}
            </Text>
          </Stack>
        </Stack>
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={useColorModeValue('white', 'gray.700')}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
        >
          {step === 'request' ? (
            <form onSubmit={handleRequestRecovery}>
              <Stack spacing="6">
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  fontSize="md"
                  isLoading={isLoading}
                >
                  Send Recovery Email
                </Button>
              </Stack>
            </form>
          ) : (
            <form onSubmit={handleRecovery}>
              <Stack spacing="6">
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input value={verifiedEmail} isReadOnly />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Recovery Phrase</FormLabel>
                  <Textarea
                    value={recoveryPhrase}
                    onChange={(e) => setRecoveryPhrase(e.target.value)}
                    placeholder="Enter your 12-word recovery phrase"
                    size="sm"
                    rows={3}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>New Password</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </FormControl>

                <Alert status="info">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text>
                      Your recovery phrase is a 12-word phrase that was shown to you during registration.
                    </Text>
                    <Text>
                      Make sure to use a strong new password that you won't forget.
                    </Text>
                  </VStack>
                </Alert>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  fontSize="md"
                  isLoading={isLoading}
                >
                  Recover Account
                </Button>
              </Stack>
            </form>
          )}
        </Box>
        <Text textAlign="center">
          Remember your password?{' '}
          <ChakraLink as={RouterLink} to="/login" color="blue.500">
            Sign in
          </ChakraLink>
        </Text>
      </Stack>
    </Container>
  );
};

export default RecoveryPage; 